import { useState } from 'react';
import * as yup from 'yup';
import { axiosInstance } from '../../../../api/axios';
import APIEndpoints from '../../../api/APIEndpoints';
import { HttpSuccessCodes } from '../../../errorhandling/HttpErrorCodes';

const usePasswordReset = ({ passwordRules, userDetails, flowName }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverError, setServerError] = useState(false);

  const Rules = {
    passMinLength: passwordRules.PasswordMinLength,
    passMaxLength: passwordRules.PasswordMaxLength,
    passNumericsReq: passwordRules.NoOfNumericsReqd,
    passSpecialCharReq: passwordRules.NoOfSpecialCharactersReqd,
    passUpperCaseReq: passwordRules.NoOfUpperCaseReqd,
    passLowerCaseReq: true,
  };

  const regexSpecialChar = new RegExp(
    /^((?=.*[-!$%^&*()/_\\+|~='"\]`{}:[;<>?,.@#]){1}).*$/,
  );

  const schema = yup.object({
    password: yup
      .string()
      .test((value, context) => {
        const { createError } = context;

        if (!value) {
          return createError({
            message: 'The Password field is required',
          });
        } else if (
          Rules.passMaxLength >= 1 &&
          !userDetails.SuperUser &&
          value.length > Rules.passMaxLength
        ) {
          return createError({
            message: `Enter a maximum of ${Rules.passMaxLength} characters only`,
          });
        } else if (
          Rules.passMinLength >= 1 &&
          value.length < (userDetails.SuperUser ? 12 : Rules.passMinLength)
        ) {
          return createError({
            message: `Enter a minimum of ${
              userDetails.SuperUser ? 12 : Rules.passMinLength
            } characters only`,
          });
        } else if (
          Rules.passUpperCaseReq >= 1 &&
          value.match('(?=.*[A-Z])') === null
        ) {
          return createError({
            message: userDetails?.SuperUser
              ? 'Enter at least one uppercase letter'
              : 'Please consult password rules',
          });
        } else if (
          Rules.passSpecialCharReq >= 1 &&
          !regexSpecialChar.test(value)
        ) {
          return createError({
            message: userDetails?.SuperUser
              ? 'Enter at least one special character'
              : 'Please consult password rules',
          });
        } else if (
          Rules.passNumericsReq >= 1 &&
          value.match('(?=.*[0-9])') === null
        ) {
          return createError({
            message: userDetails?.SuperUser
              ? 'Enter at least one numeric letter'
              : 'Please consult password rules',
          });
        } else {
          return true;
        }
      })
      .required('Enter Password'),

    confirmPassword: yup
      .string()
      .required('Enter Confirm Password')
      .when('password', {
        is: (val) => (val && val.length > 0 ? true : false),
        then: () =>
          yup.string().oneOf([yup.ref('password')], 'Passwords do not match'),
      }),
  });

  const resetPassword = async (email, password) => {
    try {
      const payload = {
        UpdateUser: {
          Oid: userDetails.Oid,
          UserID: email,
          Password: password,
          Status: userDetails.Status,
          SuperUser: userDetails.SuperUser,
        },
      };
      if (flowName) {
        payload.UpdateUser.FlowName = flowName;
      }

      const response = await axiosInstance.put(
        APIEndpoints.getUpdateUserAPIResource(),
        payload,
      );

      if (response.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_200) {
        return new Promise((resolve, reject) => {
          resolve('success');
        });
      } else {
        setServerError(true);
        return new Promise((resolve, reject) => {
          reject('We are unable to update the password now.');
        });
      }
    } catch (e) {
      if (e.response.status === 404) {
        setServerError(true);
        return new Promise((resolve, reject) => {
          reject(
            'Sorry, Unable to reset password for the user. Please contact the customer support team.',
          );
        });
      } else {
        setServerError(true);
        return new Promise((resolve, reject) => {
          reject('We are unable to update the password now.');
        });
      }
    }
  };

  return {
    schema,
    showPassword,
    showConfirmPassword,
    serverError,
    resetPassword,
    setShowPassword,
    setShowConfirmPassword,
  };
};

export default usePasswordReset;
